import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons/faArrowLeft";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";
import { useSession, signOut } from "../lib/auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Switch,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@wondermaps/ui";

export const Route = createFileRoute("/account")({
  component: AccountPage,
});

function AccountPage() {
  const navigate = useNavigate();
  const { data: session, isPending: sessionLoading } = useSession();

  // Redirect if not logged in
  if (!sessionLoading && !session?.user) {
    navigate({ to: "/login", search: { redirect: "/account" } });
    return null;
  }

  // Fetch user profile
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["user", "me"],
    queryFn: async () => {
      const res = await api.user.me.$get();
      if (!res.ok) throw new Error("Failed to fetch profile");
      return res.json();
    },
    enabled: !!session?.user,
  });

  if (sessionLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <FontAwesomeIcon icon={faSpinner} spin className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <Link
        to="/"
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
        Back
      </Link>

      <div>
        <h1 className="text-2xl font-bold">Account Settings</h1>
        <p className="text-muted-foreground">Manage your account and preferences</p>
      </div>

      <ProfileSection profile={profile} />
      <SecuritySection />
      <PreferencesSection profile={profile} />
      <DangerZone />
    </div>
  );
}

// Profile Section
function ProfileSection({ profile }: { profile: any }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(profile?.name || "");
  const [isEditing, setIsEditing] = useState(false);

  const updateProfile = useMutation({
    mutationFn: async (data: { name: string }) => {
      const res = await api.user.me.$patch({ json: data });
      if (!res.ok) throw new Error("Failed to update profile");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", "me"] });
      setIsEditing(false);
    },
  });

  const handleSave = () => {
    if (name.trim()) {
      updateProfile.mutate({ name: name.trim() });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>Your personal information</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            value={profile?.email || ""}
            disabled
            className="bg-muted"
          />
          <p className="text-xs text-muted-foreground">
            Email cannot be changed
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          {isEditing ? (
            <div className="flex gap-2">
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
              <Button
                onClick={handleSave}
                disabled={updateProfile.isPending}
                size="sm"
              >
                {updateProfile.isPending ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="h-4 w-4 " />
                ) : (
                  "Save"
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setName(profile?.name || "");
                  setIsEditing(false);
                }}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-sm">{profile?.name || "Not set"}</span>
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Security Section
function SecuritySection() {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const changePassword = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      const res = await api.user.me["change-password"].$post({ json: data });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).error || "Failed to change password");
      }
      return res.json();
    },
    onSuccess: () => {
      setIsChangingPassword(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setError(null);
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    changePassword.mutate({ currentPassword, newPassword });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Security</CardTitle>
        <CardDescription>Manage your password</CardDescription>
      </CardHeader>
      <CardContent>
        {isChangingPassword ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">New password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm new password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <div className="flex gap-2">
              <Button type="submit" disabled={changePassword.isPending}>
                {changePassword.isPending ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="h-4 w-4 mr-2" />
                ) : null}
                Change Password
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsChangingPassword(false);
                  setError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-sm">Password</p>
            <Button variant="outline" onClick={() => setIsChangingPassword(true)}>
              Change Password
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Preferences Section
function PreferencesSection({ profile }: { profile: any }) {
  const queryClient = useQueryClient();
  const prefs = profile?.preferences?.notifications || {};

  const updatePrefs = useMutation({
    mutationFn: async (notifications: {
      productUpdates?: boolean;
      supportReplies?: boolean;
      analyticsDigest?: boolean;
    }) => {
      const res = await api.user.me.$patch({
        json: { preferences: { notifications } },
      });
      if (!res.ok) throw new Error("Failed to update preferences");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", "me"] });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <CardDescription>Choose what emails you receive</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Product updates</p>
            <p className="text-xs text-muted-foreground">
              News about new features and improvements
            </p>
          </div>
          <Switch
            checked={prefs.productUpdates ?? true}
            onCheckedChange={(checked) =>
              updatePrefs.mutate({ ...prefs, productUpdates: checked })
            }
            disabled={updatePrefs.isPending}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Support replies</p>
            <p className="text-xs text-muted-foreground">
              Responses to your support requests
            </p>
          </div>
          <Switch
            checked={prefs.supportReplies ?? true}
            onCheckedChange={(checked) =>
              updatePrefs.mutate({ ...prefs, supportReplies: checked })
            }
            disabled={updatePrefs.isPending}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Analytics digest</p>
            <p className="text-xs text-muted-foreground">
              Periodic email reports about your organization's map engagement
            </p>
          </div>
          <Switch
            checked={prefs.analyticsDigest ?? true}
            onCheckedChange={(checked) =>
              updatePrefs.mutate({ ...prefs, analyticsDigest: checked })
            }
            disabled={updatePrefs.isPending}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// Danger Zone
function DangerZone() {
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const deleteAccount = useMutation({
    mutationFn: async () => {
      const res = await api.user.me.$delete();
      if (!res.ok) throw new Error("Failed to delete account");
      return res.json();
    },
    onSuccess: async () => {
      await signOut();
      navigate({ to: "/login", search: { redirect: "/" } });
    },
  });

  const handleDelete = () => {
    if (confirmText === "DELETE") {
      deleteAccount.mutate();
    }
  };

  return (
    <Card className="border-destructive/50">
      <CardHeader>
        <CardTitle className="text-destructive">Danger Zone</CardTitle>
        <CardDescription>
          Irreversible actions that affect your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Delete account</p>
            <p className="text-xs text-muted-foreground">
              Permanently delete your account and all data
            </p>
          </div>
          <Button
            variant="destructive"
            onClick={() => setShowConfirm(true)}
          >
            Delete Account
          </Button>
        </div>

        <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Account</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p>
                    This action cannot be undone. This will permanently delete your
                    account and remove all your data.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="confirm">
                      Type <strong>DELETE</strong> to confirm
                    </Label>
                    <Input
                      id="confirm"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="DELETE"
                    />
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmText("")}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={confirmText !== "DELETE" || deleteAccount.isPending}
                className="bg-muted text-destructive hover:bg-muted/80"
              >
                {deleteAccount.isPending ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="h-4 w-4 mr-2" />
                ) : null}
                Delete Account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
